import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {API} from "../../../types/API";
import {AxiosError, AxiosResponse} from "axios";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {WithdrawProvider} from "../../../types/WithdrawProvider";
import OutlineInput from "../../../components/OutlineInput";
import {Button} from "@material-tailwind/react";
import Loader from "../../../assets/icons/Loader";
import {InputType} from "../../../types/InputType";
import {setErrors, validateData} from "../../../reducers/validationReducer";
import {useDispatch} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {emailRegex, nameRegEx} from "./WithdrawsForm";
import {Input} from "../../../types/Input";
import {useTranslation} from "react-i18next";
import {APILink} from "../../../types/APILink";

interface LimePayFormProps {
    broker: number,
    clientTransactionID?: number
}

const LimePayForm = ({broker, clientTransactionID}: LimePayFormProps) => {

    const {t} = useTranslation();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const [amount, setAmount] = useState<number>();
    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [message, setMessage] = useState<string>('');
    const [documentID, setDocumentID] = useState<string>('');
    const [bankAccount, setBankAccount] = useState<string>('');
    const [bankBranch, setBankBranch] = useState<string>('');
    const [beneficiaryName, setBeneficiaryName] = useState<string>('');

    const [validated, setValidated] = useState<boolean>(false);
    const [isInProgress, setInProgress] = useState<boolean>(false);

    const dispatch = useDispatch();

    const clearData = () => {
        setInProgress(false);
        setAmount(0);
        setFirstName('');
        setLastName('');
        setEmail('');
        setValidated(false);
        setDocumentID('');
        setBankAccount('');
        setBankBranch('');
        setBeneficiaryName('');
    }


    const getClientTransaction = useCallback(() => {
        API.getWithdrawTransaction(clientTransactionID ?? 0).then((response: AxiosResponse) => {
            setFirstName(response.data.first_name);
            setLastName(response.data.last_name);
            setEmail(response.data.email);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WITHDRAW_TRANSACTION.replace('$transaction', (clientTransactionID ?? 0).toString())}`, toast, handleError));
    }, [clientTransactionID, toast, handleError]);

    const changeAmount = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAmount(parseFloat(e.currentTarget.value))
        }
    }, [setValidated, setAmount]);

    const changeFirstName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setFirstName(e.currentTarget.value)
        }
    }, [setValidated, setFirstName]);

    const changeLastName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setLastName(e.currentTarget.value)
        }
    }, [setValidated, setLastName]);


    const changeEmail = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setEmail(e.currentTarget.value)
        }
    }, [setValidated, setEmail]);

    const changeMessage = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setMessage(e.currentTarget.value)
        }
    }, [setValidated, setMessage]);

    const changeDocumentID = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setDocumentID(e.currentTarget.value);
        }
    }, [setValidated, setDocumentID]);

    const changeBankAccount = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setBankAccount(e.currentTarget.value);
        }
    }, [setValidated, setBankAccount]);

    const changeBankBranch = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setBankBranch(e.currentTarget.value);
        }
    }, [setValidated, setBankBranch]);

    const changeBeneficiaryName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setBeneficiaryName(e.currentTarget.value);
        }
    }, [setValidated, setBeneficiaryName]);


    const handleSuccessResponse = useCallback((response: AxiosResponse) => {
        if (response.data.data) {
            clearData();
            const status = response.data.data.status;
            let message = '';
            if (status && status === 'Waiting for approve')
                message = t('admin.withdraws.approve_is_required')
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {t('admin.withdraws.created_message')}<span
                className="uppercase inline-block ml-1">{message}</span></>, "bg-teal-500");
        }
    }, [t, toast]);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        if (b) {
            const request = {
                bank_account: bankAccount,
                bank_branch: bankBranch,
                beneficiary_name: beneficiaryName,
                document_id: documentID,
                amount,
                first_name: firstName,
                last_name: lastName,
                email,
                message,
                country: 'IN',
                currency: 'INR',
                broker,
                provider: WithdrawProvider.LIMEPAY,
            };
            API.makeLimePayWithdraw(request)
                .then((response: AxiosResponse) => handleSuccessResponse(response))
                .catch((e) => {
                    setInProgress(false);
                    if (e instanceof AxiosError && e.response) {
                        setErrors(e.response.data.errors);
                    }
                    ErrorHandler(e, `post${APILink.MAKE_LIMEPAY_WITHDRAW}`, toast, handleError)
                })
        } else setInProgress(false);
    }, [bankAccount, bankBranch, beneficiaryName, documentID, amount, firstName, lastName, email, message, broker, handleSuccessResponse, toast, handleError]);

    const submit = useCallback(() => {
        setInProgress(true);
        const inputs: Input[] = [
            {
                key: 'document_id',
                label: t('admin.withdraws.document_id'),
                type: InputType.number,
                regex: '^\\d{12,12}$'
            },
            {
                key: 'bank_account',
                label: t('admin.withdraws.bank_account'),
                type: InputType.number,
                regex: '^[0-9-.]{9,18}$'
            },
            {
                key: 'bank_branch',
                label: t('admin.withdraws.bank_branch'),
                type: InputType.text,
                regex: '^[A-Z|a-z]{4}[0][a-zA-Z0-9]{6}$'
            },
            {
                key: 'beneficiary_name',
                label: t('admin.withdraws.beneficiary_name'),
                type: InputType.text,
                regex: '^(?=.{1,100}$)[a-zA-Z]+(?:[\'_.\\s][a-zA-Z]+)*$'
            },
            {key: 'account', label: t('admin.withdraws.currency'), type: InputType.number},
            {key: 'amount', label: t('admin.withdraws.amount'), type: InputType.number, regex: '^\\d{1,18}$'},
            {key: 'first_name', label: t('admin.withdraws.first_name'), type: InputType.number, regex: nameRegEx},
            {key: 'last_name', label: t('admin.withdraws.last_name'), type: InputType.number, regex: nameRegEx},
            {key: 'email', label: t('admin.withdraws.email'), type: InputType.email, regex: emailRegex},
        ];

        if (message.length) {
            inputs.push({key: 'message', label: t('admin.withdraws.message'), type: InputType.text, regex: '^.{5,}'});
        }

        const data = {
            bank_account: bankAccount,
            bank_branch: bankBranch,
            beneficiary_name: beneficiaryName,
            document_id: documentID,
            amount,
            first_name: firstName,
            last_name: lastName,
            email,
            message,
        };

        dispatch(validateData({
            payload: {
                inputs: inputs,
                introducedData: data,
                callback: callback
            }
        }));

    }, [t, message, bankAccount, bankBranch, beneficiaryName, documentID, amount, firstName, lastName, email, dispatch, callback]);


    const amountUI = useMemo(() => {
        if (!beneficiaryName || !bankAccount || !bankBranch)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.amount_in', {currency: 'INR'})} type="number" validated={validated}
                          value={amount}
                          fieldName="amount"
                          onChange={changeAmount}/>
        </div>
    }, [beneficiaryName, bankAccount, bankBranch, t, validated, amount, changeAmount]);

    const firstNameUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.first_name')} type="text" validated={validated} value={firstName}
                          fieldName="first_name"
                          onChange={changeFirstName}/>
        </div>
    }, [t, validated, firstName, changeFirstName]);

    const documentIDUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.document_id')} type="text" validated={validated} value={documentID}
                          fieldName="document_id"
                          onChange={changeDocumentID}/>
        </div>
    }, [t, validated, documentID, changeDocumentID]);

    const bankAccountUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.bank_account')} type="text" validated={validated} value={bankAccount}
                          fieldName="bank_account"
                          onChange={changeBankAccount}/>
        </div>
    }, [t, validated, bankAccount, changeBankAccount]);

    const bankBranchUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.bank_branch')} type="text" validated={validated} value={bankBranch}
                          fieldName="bank_branch"
                          onChange={changeBankBranch}/>
        </div>
    }, [t, validated, bankBranch, changeBankBranch]);

    const beneficiaryNameUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.beneficiary_name')} type="text" validated={validated}
                          value={beneficiaryName}
                          fieldName="bank_branch"
                          onChange={changeBeneficiaryName}/>
        </div>
    }, [t, validated, beneficiaryName, changeBeneficiaryName]);

    const lastNameUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.last_name')} type="text" validated={validated} value={lastName}
                          fieldName="last_name"
                          onChange={changeLastName}/>
        </div>
    }, [t, validated, lastName, changeLastName]);

    const emailUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.email')} type="email" validated={validated} value={email}
                          fieldName="email"
                          onChange={changeEmail}/>
        </div>
    }, [t, validated, email, changeEmail])

    const messageUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.message')} type="text" validated={validated} value={message}
                          fieldName="message"
                          onChange={changeMessage}/>
        </div>
    }, [t, validated, message, changeMessage])


    const submitButtonUI = useMemo(() => {
        if (!amount)
            return <></>;

        return <div className="max-w-md mt-8">
            <Button className="bg-teal-500 text-white"
                    onClick={() => submit()}
                    disabled={isInProgress}>{isInProgress ?
                <Loader/> : t('Submit')}</Button>
        </div>
    }, [amount, isInProgress, submit, t]);


    useEffect(() => {
        clearData();
        if (clientTransactionID) {
            getClientTransaction();
        }
    }, [broker, clientTransactionID, getClientTransaction]);

    return <>
        {firstNameUI}
        {lastNameUI}
        {emailUI}
        {documentIDUI}
        {bankAccountUI}
        {bankBranchUI}
        {beneficiaryNameUI}
        {messageUI}
        {amountUI}
        {submitButtonUI}
    </>
}

export default memo(LimePayForm);
